import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { catchError, filter, Observable, switchMap, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { TranslatePipe } from '@ngx-translate/core';
import { NewsState } from 'src/app/shared/states/news/news.state';
import { IUser } from 'src/app/shared/interfaces/IUser';
import { LoadOrgUnits } from 'src/app/shared/states/orgUnits/orgUnit.actions';
import { IOrgUnit } from 'src/app/shared/interfaces/orgunit/IOrgUnit';
import { OrgUnitState } from 'src/app/shared/states/orgUnits/orgUnit.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NewsOperations } from 'src/app/shared/operations/news.operations';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { OrderByPipe } from 'src/app/shared/pipes/order-by.pipe';
import { MatInput } from '@angular/material/input';
import { CreateNews, LoadAllActiveNews } from 'src/app/shared/states/news/news.actions';
import { AccountsOperations } from 'src/app/accounts/operations/accounts.operations';
import { UserOperations } from 'src/app/shared/operations/user.operations';
import { USER_ROLES } from 'src/app/shared/enums/UserRoles.enum';
import { NEWS_TYPES } from 'src/app/shared/enums/NewsTypes.enum';
import { DatepickerHeaderComponent } from '../../../datepicker-header/datepicker-header.component';
import { FormControlPipe } from '../../../../pipes/formControl.pipe';
import { LoadingComponent } from '../../../loading/loading.component';
import { UniversalDropdownComponent } from '../../../universal-dropdown/universal-dropdown.component';
import { NotificationService } from '../../../../services/notification.service';
import { LoadingMode } from '../../../../enums/LoadingMode.enum';
import { UserState } from '../../../../states/user.state';

@Component({
  selector: 'app-create-news-dialog',
  templateUrl: './create-news-dialog.component.html',
  styleUrls: ['./create-news-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    UniversalDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    LoadingComponent,
    MatDialogActions,
    FormsModule,
    MatFormField,
    MatLabel,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    OrderByPipe,
    LoadingComponent,
    MatDatepicker,
    ReactiveFormsModule,
    TranslatePipe,
    FormControlPipe,
    MatInput,
  ],
})
export class CreateNewsDialogComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<CreateNewsDialogComponent>>(MatDialogRef);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private notificationService = inject(NotificationService);
  protected isLoadingNews = this.store.selectSignal<boolean>(NewsState.isLoading);
  protected isLoadingUser = this.store.selectSignal<boolean>(UserState.isLoading);
  protected isLoadingOrgUnits = this.store.selectSignal<boolean>(OrgUnitState.isLoading);
  protected orgUnits: IOrgUnit[] = [];
  protected orgUnitsGroups: string[] = [];
  protected orgTeamsForGroup: string[] = [];
  protected previousGroupValue: string;
  protected newsForm: FormGroup;
  protected DatepickerHeaderComponent = DatepickerHeaderComponent;
  protected todaysDate = new Date();
  protected readonly LoadingMode = LoadingMode;
  protected readonly UserOperations = UserOperations;
  protected readonly userRoles: string[] = Object.values(USER_ROLES);
  protected readonly newsTypes: string[] = Object.values(NEWS_TYPES);
  protected readonly NEWS_TYPES = NEWS_TYPES;
  protected user = this.store.selectSignal<IUser>(UserState.user);
  private orgUnits$ = this.store.select<IOrgUnit[]>(OrgUnitState.orgUnits);

  ngOnInit(): void {
    this.getOrgUnits();
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    this.newsForm.markAllAsTouched();
    if (this.newsForm.valid) {
      const newsCreateItem = NewsOperations.getNewsCreateItemFromForm(this.newsForm, this.orgUnits);
      this.store
        .dispatch(new CreateNews(newsCreateItem))
        .pipe(
          tap(() => {
            NewsOperations.showCreateNewsSuccessNotification(this.notificationService);
            this.closeDialog();
          }),
          catchError(() => NewsOperations.showCreateNewsErrorNotification(this.notificationService)),
          switchMap(() => this.store.dispatch(new LoadAllActiveNews())),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    } else {
      AccountsOperations.showRequiredColumnsMessage(this.notificationService);
    }
  }

  private getOrgUnits(): void {
    this.store.dispatch(new LoadOrgUnits());
    this.orgUnits$
      .pipe(
        filter(orgUnits => orgUnits.length > 0),
        tap(orgUnits => this.initializeOrgUnitsAndBuildForm(orgUnits)),
        switchMap(() => this.listenToNewsTypeChanges()),
        switchMap(() => this.listenToGroupChanges()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private buildForm(): void {
    this.newsForm = NewsOperations.buildCreateNewsForm(this.user());
  }

  private listenToNewsTypeChanges(): Observable<string> {
    return this.newsForm.get('newsType').valueChanges.pipe(
      tap((newsType: NEWS_TYPES) => {
        switch (newsType) {
          case NEWS_TYPES.GROUP_AND_TEAM:
            this.newsForm.patchValue({ isForAll: false, userRole: null });
            this.newsForm.get('group').setValidators([Validators.required]);
            this.newsForm.get('team').setValidators([Validators.required]);
            this.newsForm.get('isForAll').clearValidators();
            this.newsForm.get('userRole').clearValidators();
            this.newsForm.updateValueAndValidity();
            break;
          case NEWS_TYPES.ALL_EMPLOYEES:
            this.newsForm.patchValue({ isForAll: true, group: null, team: null, userRole: null });
            this.newsForm.get('group').clearValidators();
            this.newsForm.get('team').clearValidators();
            this.newsForm.get('userRole').clearValidators();
            this.newsForm.get('isForAll').setValidators([Validators.required]);
            this.newsForm.updateValueAndValidity();
            break;
          case NEWS_TYPES.USER_ROLE:
            this.newsForm.patchValue({ isForAll: false, group: null, team: null });
            this.newsForm.get('group').clearValidators();
            this.newsForm.get('team').clearValidators();
            this.newsForm.get('isForAll').clearValidators();
            this.newsForm.get('userRole').setValidators([Validators.required]);
            this.newsForm.updateValueAndValidity();
            break;
          default:
            break;
        }
      }),
    );
  }

  private listenToGroupChanges(): Observable<string> {
    return this.newsForm.get('group').valueChanges.pipe(
      tap((value: string) => {
        if (value !== this.previousGroupValue) {
          this.previousGroupValue = value;
          this.newsForm.patchValue({ team: null });
        }
        this.orgTeamsForGroup = this.orgUnits.filter(orgUnit => orgUnit.groupName === value).map(orgUnit => orgUnit.team);
      }),
    );
  }

  private initializeOrgUnitsAndBuildForm(orgUnits: IOrgUnit[]): void {
    this.orgUnits = orgUnits.filter(orgUnit => new Date(orgUnit.inactive).getTime() >= new Date().getTime());
    this.orgUnitsGroups = Array.from(new Set(this.orgUnits.map(x => x.groupName)));
    this.previousGroupValue = UserOperations.isUserTechnicalController(this.user()) ? null : this.user().team.groupName;
    this.buildForm();
  }
}
